import {
  MAX_AFILHADOS_LIMIT,
  myAfilhadosSchema,
  type Afilhado,
  type MyAfilhados,
} from '@growup/streaming-shared';
import { Button, Card, Field, Input, Notice, StatusBadge, Textarea } from '@growup/ui';
import { useCallback, useEffect, useState } from 'react';

import { getJson, messageOf, send } from '../api';
import { SubNav } from '../members/MembersSection';
import { formatDate } from '../format';
import { ChecklistPanel } from './ChecklistPanel';
import { CheckupForm } from './CheckupView';
import { ConversationView } from './ConversationView';
import { SemaforoBadges } from './SemaforoBadges';
import { formatMinutes, STREAMER_LOOK } from './labels';

/**
 * "Os meus afilhados" (GS1, `streaming.padrinho`): the padrinho or madrinha
 * sees only their own afilhados, sets how many they can take, and can stop
 * following one (the Stream Staff gets an email to assign another).
 */
export function MyAfilhadosView() {
  const [data, setData] = useState<MyAfilhados | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    getJson('/api/streaming/padrinho/me', myAfilhadosSchema)
      .then(setData)
      .catch((e: unknown) => setError(messageOf(e)));
  }, []);
  useEffect(load, [load]);

  return (
    <div className="flex flex-col gap-4">
      {error ? <Notice tone="danger">{error}</Notice> : null}
      {data ? <MaxCard data={data} onSaved={setData} /> : null}
      {data && data.afilhados.length === 0 ? (
        <p className="text-gu-ink-muted">
          Ainda não tem afilhados. O Stream Staff atribui-os em Streaming &gt; Padrinhos.
        </p>
      ) : null}
      {data?.afilhados.map((a) => (
        <AfilhadoCard key={a.id} afilhado={a} onLeft={setData} />
      ))}
    </div>
  );
}

function MaxCard({ data, onSaved }: { data: MyAfilhados; onSaved: (d: MyAfilhados) => void }) {
  const [value, setValue] = useState(String(data.maxAfilhados));
  const [message, setMessage] = useState<{ tone: 'danger' | 'success'; text: string } | null>(null);
  const n = Number(value);
  const valid = value !== '' && Number.isInteger(n) && n >= 0 && n <= MAX_AFILHADOS_LIMIT;

  const save = async () => {
    setMessage(null);
    try {
      onSaved(
        myAfilhadosSchema.parse(
          await send('PUT', '/api/streaming/padrinho/me', { maxAfilhados: n }),
        ),
      );
      setMessage({ tone: 'success', text: 'Guardado.' });
    } catch (e) {
      setMessage({ tone: 'danger', text: messageOf(e) });
    }
  };

  return (
    <Card
      headingLevel={2}
      title={`${String(data.afilhados.length)} de ${String(data.maxAfilhados)} afilhados`}
      eyebrow="Ajuste o máximo à sua disponibilidade. O Stream Staff só atribui até lá."
    >
      <div className="flex flex-wrap items-end gap-2">
        <Field label="Quantos afilhados posso acompanhar" className="w-64">
          <Input
            type="number"
            min={0}
            max={MAX_AFILHADOS_LIMIT}
            value={value}
            onChange={(e) => setValue(e.target.value)}
          />
        </Field>
        <Button
          size="sm"
          className="mb-1.5"
          disabled={!valid || n === data.maxAfilhados}
          onClick={() => void save()}
        >
          Guardar
        </Button>
      </div>
      {message ? <Notice tone={message.tone}>{message.text}</Notice> : null}
    </Card>
  );
}

function AfilhadoCard({
  afilhado: a,
  onLeft,
}: {
  afilhado: Afilhado;
  onLeft: (d: MyAfilhados) => void;
}) {
  const [leaving, setLeaving] = useState(false);
  // Separadores do cartao (arrumar a gestao, parte 4): um de cada vez.
  const [view, setView] = useState<'resumo' | 'relatorio' | 'checklist' | 'mensagens'>('resumo');
  const [due, setDue] = useState(a.checkup);
  const [unread, setUnread] = useState(a.unreadMessages);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const leave = async () => {
    setBusy(true);
    setError(null);
    try {
      onLeft(
        myAfilhadosSchema.parse(
          await send('POST', `/api/streaming/padrinho/afilhados/${a.id}/leave`, { reason }),
        ),
      );
    } catch (e) {
      setError(messageOf(e));
      setBusy(false);
    }
  };

  return (
    <Card
      headingLevel={2}
      eyebrow={`${a.confirmed ? 'Afilhado' : 'Afilhado provisório'} desde ${formatDate(a.since)} · conta ${a.login}`}
      title={
        <a
          className="gu-link"
          href={`https://www.twitch.tv/${a.channel}`}
          target="_blank"
          rel="noreferrer"
        >
          {a.displayName}
        </a>
      }
      actions={
        a.live ? (
          <StatusBadge tone="success">No ar · {a.live.viewers}</StatusBadge>
        ) : a.inactive ? (
          <StatusBadge tone="warning">Inativo · {a.daysWithoutStream} dias</StatusBadge>
        ) : (
          <StatusBadge tone={STREAMER_LOOK[a.status].tone}>
            {STREAMER_LOOK[a.status].label}
          </StatusBadge>
        )
      }
    >
      <SubNav
        label={`Acompanhar ${a.displayName}`}
        tabs={[
          { id: 'resumo' as const, label: 'Resumo' },
          { id: 'relatorio' as const, label: 'Fazer relatório' },
          { id: 'checklist' as const, label: 'Checklist dos 30 dias' },
          {
            id: 'mensagens' as const,
            label:
              unread > 0
                ? `Mensagens (${String(unread)} ${unread === 1 ? 'nova' : 'novas'})`
                : 'Mensagens',
          },
        ]}
        current={view}
        onChange={setView}
      />
      {view === 'resumo' ? (
        <>
          <p className="text-gu-ink-muted">
            Última transmissão:{' '}
            {a.lastStreamAt ? formatDate(a.lastStreamAt) : 'nenhuma desde que entrou'}
            {' · '}Últimos 30 dias: {a.last30.sessions}×, {formatMinutes(a.last30.minutesLive)}
          </p>
          <p className="text-gu-ink-muted">
            {a.intro
              ? `Conversa de apresentação a ${formatDate(`${a.intro.on}T12:00:00Z`)} com ${a.intro.by}`
              : 'Conversa de apresentação por fazer: combine-a com alguém do Stream Staff.'}
          </p>
          {a.semaforo ? (
            <p
              className="flex flex-wrap items-center gap-2"
              aria-label={`Semáforo de ${a.displayName}`}
            >
              <SemaforoBadges semaforo={a.semaforo} />
              <span className="text-caption text-gu-ink-muted">
                {a.semaforo.lastContactAt
                  ? `Último contacto: ${formatDate(a.semaforo.lastContactAt)}`
                  : 'Ainda sem contacto registado (mensagem ou relatório)'}
              </span>
            </p>
          ) : null}
          {due ? (
            <p className={due.overdue ? 'font-semibold text-gu-danger' : 'text-gu-ink-muted'}>
              {due.overdue
                ? `Relatório em atraso desde ${formatDate(due.dueOn)}`
                : `Próximo relatório até ${formatDate(due.dueOn)}`}
              {due.lastAt ? ` · último a ${formatDate(due.lastAt)}` : ''}
            </p>
          ) : null}
        </>
      ) : null}
      {view === 'relatorio' ? (
        <CheckupForm
          streamerId={a.id}
          onSent={(d) => {
            setDue(d.due);
            setView('resumo');
          }}
        />
      ) : null}
      {view === 'checklist' ? (
        <ChecklistPanel base={`/api/streaming/padrinho/afilhados/${a.id}/checklist`} />
      ) : null}
      {view === 'mensagens' ? (
        <ConversationView
          path={`/api/streaming/padrinho/afilhados/${a.id}/messages`}
          canWrite
          onRead={() => setUnread(0)}
        />
      ) : null}
      {view !== 'resumo' ? null : leaving ? (
        <div className="flex flex-col gap-2">
          <Field
            label="Porque deixa de acompanhar"
            hint="Vai no email ao Stream Staff, que atribui outro padrinho."
          >
            <Textarea value={reason} maxLength={1000} onChange={(e) => setReason(e.target.value)} />
          </Field>
          {error ? <Notice tone="danger">{error}</Notice> : null}
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="danger"
              disabled={busy || reason.trim().length < 3}
              onClick={() => void leave()}
            >
              Confirmar: deixar de acompanhar
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setLeaving(false)}>
              Cancelar
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="ghost" onClick={() => setLeaving(true)}>
            Deixar de acompanhar
          </Button>
        </div>
      )}
    </Card>
  );
}
